import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import { AuthContext } from "./AuthContext";

const serverUrl = process.env.REACT_APP_SERVER_URL;

const MaintenanceContext = createContext(null);

export const MaintenanceProvider = ({ children }) => {
  const { user, primaryRole } = useContext(AuthContext);

  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);
  const [maintenanceInfo, setMaintenanceInfo] = useState(null);
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  const isSuperAdmin =
    primaryRole === "superadmin" ||
    user?.role === "superadmin" ||
    (Array.isArray(user?.role) && user.role.includes("superadmin"));

  const activeDocIdRef = useRef(null);
  const lastCheckedRef = useRef(null);

  // Check maintenance status from backend
  const checkMaintenanceStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${serverUrl}/api/maintenance/status`, {
        withCredentials: true,
        timeout: 8000,
      });

      if (response.data) {
        const {
          isUnderMaintenance: underMaint,
          isScheduled: sched,
          id,
          title,
          reason,
          startDateTime,
          estimatedHours,
          isActive,
        } = response.data;

        setIsUnderMaintenance(Boolean(underMaint));
        setIsScheduled(Boolean(sched));
        setMaintenanceInfo({
          id,
          title: title || "Scheduled Platform Maintenance",
          reason:
            reason ||
            "We are currently upgrading our platform to provide a better experience.",
          startDateTime,
          estimatedHours: estimatedHours ?? 2,
          isActive,
        });

        activeDocIdRef.current = id;
        const checkTime = new Date();
        lastCheckedRef.current = checkTime;
        setLastChecked(checkTime);

        // Check if user previously dismissed this specific maintenance window banner
        if (id) {
          const dismissedId = sessionStorage.getItem(
            "maintenance_banner_dismissed_id",
          );
          if (dismissedId === id) {
            setIsBannerDismissed(true);
          } else {
            setIsBannerDismissed(false);
          }
        }

        return response.data;
      }
      return null;
    } catch (error) {
      // If the status check fails with 503, the backend is definitely in maintenance mode
      if (error.response && error.response.status === 503) {
        const data = error.response.data || {};
        setIsUnderMaintenance(true);
        if (data.reason || data.startDateTime) {
          setMaintenanceInfo({
            title: data.title || "Scheduled Platform Maintenance",
            reason: data.reason || "We are currently upgrading our platform.",
            startDateTime: data.startDateTime,
            estimatedHours: data.estimatedHours ?? 2,
          });
        }
        return { isUnderMaintenance: true, ...data };
      }
      return null;
    }
  }, []);

  // Dismiss scheduled banner for this session
  const dismissBanner = useCallback(() => {
    setIsBannerDismissed(true);
    if (activeDocIdRef.current) {
      sessionStorage.setItem(
        "maintenance_banner_dismissed_id",
        activeDocIdRef.current,
      );
    }
  }, []);

  // Listen to custom window event dispatched when any API call receives a 503 MAINTAINANCE_MODE
  useEffect(() => {
    const handleMaintenanceTrigger = (event) => {
      const detail = event.detail || {};
      setIsUnderMaintenance(true);
      setMaintenanceInfo((prev) => ({
        ...prev,
        title: detail.title || prev?.title || "Scheduled Platform Maintenance",
        reason:
          detail.reason ||
          prev?.reason ||
          "We are currently working on making your experience better.",
        startDateTime: detail.startDateTime || prev?.startDateTime,
        estimatedHours: detail.estimatedHours ?? prev?.estimatedHours ?? 2,
      }));
    };

    window.addEventListener(
      "maintenance-mode-triggered",
      handleMaintenanceTrigger,
    );
    return () => {
      window.removeEventListener(
        "maintenance-mode-triggered",
        handleMaintenanceTrigger,
      );
    };
  }, []);

  // Global Axios response interceptor for 503 maintenance mode responses
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 503) {
          const data = error.response.data;
          if (
            data &&
            (data.error === "MAINTAINANCE_MODE" || data.statusCode === 503)
          ) {
            window.dispatchEvent(
              new CustomEvent("maintenance-mode-triggered", { detail: data }),
            );
          }
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  // 1. Initial check on mount
  useEffect(() => {
    checkMaintenanceStatus();
  }, [checkMaintenanceStatus]);

  // 2. Re-check when user refocuses or returns to the tab (throttled to at least 5 minutes)
  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === "visible") {
        const lastTime = lastCheckedRef.current
          ? lastCheckedRef.current.getTime()
          : 0;
        const timeSinceLastCheck = Date.now() - lastTime;
        // Only re-verify if at least 5 minutes have passed since the previous check
        if (timeSinceLastCheck > 5 * 60 * 1000) {
          checkMaintenanceStatus();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityOrFocus);
    window.addEventListener("focus", handleVisibilityOrFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
      window.removeEventListener("focus", handleVisibilityOrFocus);
    };
  }, [checkMaintenanceStatus]);

  // 3. If maintenance is scheduled in the future, set an exact timeout for when it starts
  useEffect(() => {
    if (isScheduled && maintenanceInfo?.startDateTime) {
      const startTime = new Date(maintenanceInfo.startDateTime).getTime();
      const now = Date.now();
      const diff = startTime - now;

      if (diff > 0 && diff < 86400000) {
        // Less than 24 hours away
        const timer = setTimeout(() => {
          setIsScheduled(false);
          setIsUnderMaintenance(true);
          checkMaintenanceStatus();
        }, diff);
        return () => clearTimeout(timer);
      } else if (diff <= 0) {
        setIsScheduled(false);
        setIsUnderMaintenance(true);
      }
    }
  }, [isScheduled, maintenanceInfo?.startDateTime, checkMaintenanceStatus]);

  const now = Date.now();
  const startTime = maintenanceInfo?.startDateTime
    ? new Date(maintenanceInfo.startDateTime).getTime()
    : null;

  const dynamicIsUnderMaintenance = Boolean(
    isUnderMaintenance ||
      (maintenanceInfo?.isActive && startTime && now >= startTime),
  );

  const dynamicIsScheduled = Boolean(
    isScheduled &&
      startTime &&
      now < startTime &&
      maintenanceInfo?.isActive &&
      !dynamicIsUnderMaintenance,
  );

  // 4. ACTIVE MAINTENANCE POLLING:
  // ONLY poll when the app is ACTUALLY in maintenance mode and tab is visible.
  // This allows the maintenance screen to automatically detect when maintenance finishes and recover,
  // without spamming the server with 24/7 background polls during normal operation.
  useEffect(() => {
    if (!dynamicIsUnderMaintenance || previewMode) return;

    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        checkMaintenanceStatus();
      }
    }, 30000); // Poll every 30s ONLY while actively under maintenance

    return () => clearInterval(interval);
  }, [dynamicIsUnderMaintenance, previewMode, checkMaintenanceStatus]);

  return (
    <MaintenanceContext.Provider
      value={{
        isUnderMaintenance: previewMode ? true : dynamicIsUnderMaintenance,
        realIsUnderMaintenance: dynamicIsUnderMaintenance,
        isScheduled: dynamicIsScheduled,
        maintenanceInfo,
        isBannerDismissed,
        dismissBanner,
        checkMaintenanceStatus,
        previewMode,
        setPreviewMode,
        isSuperAdmin,
        lastChecked,
      }}
    >
      {children}
    </MaintenanceContext.Provider>
  );
};

export const useMaintenance = () => {
  const context = useContext(MaintenanceContext);
  if (!context) {
    throw new Error("useMaintenance must be used within a MaintenanceProvider");
  }
  return context;
};

export default MaintenanceContext;
